import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import { rem } from 'polished'
import styled from 'styled-components'
import { withRouter } from 'react-router'
import { connect } from 'react-redux'
import { RouteComponentProps } from 'react-router-dom'
import config from 'constants/config'
import { CRUISE_CHECKOUT_INFLOW } from 'constants/checkout'
import { useAppDispatch } from 'hooks/reduxHooks'
import useToggle from 'hooks/useToggle'
import { cabinViewMap } from 'api/mappers/cruiseMap'
import { isoDateFormat, addDays, convertTZ } from 'lib/dateUtils'
import { generateCruiseCheckoutItem } from 'checkout/lib/utils/cruises/cart'
import { addToCheckoutCart, removeItem } from 'actions/CheckoutActions'
import { arrayOfYears } from 'lib/Cruise/cruiseDateUtils'
import { ShareModalConfig } from 'lib/getShareModalConfig'
import { fetchCabinRates, resetCabinRates, setInFlowCheckoutStep } from 'actions/CruiseActions'
import { CruiseContentSections, CRUISE_CONTENT_SECTION_LABELS, CruiseSectionTabValues } from 'constants/cruise'
import { EXPERIMENT, startExperiment } from 'lib/optimize'
import { pushWithRegion, replaceWithRegion } from 'actions/NavigationActions'
import CruiseFAQ from './FAQ/CruiseFAQ'
import Pane from 'components/Common/Pane'
import AboutTheShip from './AboutTheShip'
import CruiseHeadData from './CruiseHeadData'
import CruiseOverview from './CruiseOverview'
import Modal from 'components/Luxkit/Modal/Modal'
import TabButton from 'components/Luxkit/Tabs/TabButton'
import MultipleCabinsCallUs from './MultipleCabinsCallUs'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import CruiseFinePrint from './FinePrint/CruiseFinePrint'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Heading from 'components/Luxkit/Typography/Heading'
import ImageGallery from 'components/Luxkit/Carousel/ImageGallery'
import BodyText from 'components/Luxkit/Typography/BodyText'
import CruiseDepartures from './Departures/CruiseDepartures'
import UrgencyLabel from 'components/Luxkit/Label/UrgencyLabel'
import LayoutContainer from 'components/Common/LayoutContainer'
import { offerHasValidDepartures } from 'lib/Cruise/cruiseUtils'
import TabButtonsGroup from 'components/Luxkit/Tabs/TabButtonsGroup'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CruiseItineraryAndMap from './Itinerary/CruiseItineraryAndMap'
import CruiseDecksAndCabins from './DecksAndCabins/CruiseDecksAndCabins'
import CruiseDepartureDatesTabs from './Departures/CruiseDepartureDatesTabs'
import CabinSelectionModal from './CabinSelectionModal/CruiseCabinSelectionModal'
import determineCruiseContentSections from 'lib/Cruise/determineCruiseContentSections'
import TravelerSelectionModal from './TravelerSelectionModal/CruiseTravelerSelectionModal'
import { pluralizeToString } from 'lib/pluralize'
import { fireInteractionEvent } from 'api/googleTagManager'
import { cruiseSelectDepartureClick } from 'analytics/eventDefinitions'
import CruiseVerticalUSPs from './BookWithConfidence/CruiseVerticalUSPs'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import CruiseStickyFooter from './StickyElements/CruiseStickyFooter'
import PaletteProvider from 'contexts/PaletteProvider'
import { VIRGIN_VELOCITY_PARTNERSHIP_CODE } from 'constants/partnerships'
import useQueryParams from 'hooks/useQueryParams'
import { min } from 'lib/arrayUtils'
import { replace } from 'connected-react-router'
import CruiseStickyHeader from './StickyElements/CruiseStickyHeader'
import { getCruiseItems } from 'checkout/selectors/view/cruise'
import PartnershipLabel from 'components/Luxkit/Label/PartnershipLabel'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import { getApplicableVelocityFeatures } from 'lib/partnershipUtils'
import { OFFER_TYPE_CRUISE } from 'constants/offer'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import CruiseRecommendations from 'components/Common/OfferRecommendations/CruiseRecommendations'
import { fetchPlaceByName } from 'actions/PlaceActions'

const OCCUPANCY_INITIAL_STATE: Array<App.Occupants> = [{
  adults: 2,
  infants: 0,
  children: 0,
  childrenAge: [],
}]

const USPPane = styled(Pane)`
  padding: ${rem(40)} 0;
`
const GalleryContainer = styled(LayoutContainer)`
  padding: 0;

  ${mediaQueryUp.sm} {
    padding: ${rem(24)} ${rem(16)} 0;
  }
`

const SectionContainer = styled(LayoutContainer)`
  padding-bottom: ${rem(32)};
  padding-top: ${rem(32)};

  > .section-title + .section-subtitle {
    margin-top: ${rem(8)};
  }

  > .section-title + .section-content,
  > .section-subtitle + .section-content {
    margin-top: ${rem(24)};
  }

  ${mediaQueryUp.xs} {
    padding-bottom: ${rem(64)};
    padding-top: ${rem(64)};

    > .section-title + .section-content,
    > .section-subtitle + .section-content {
      margin-top: ${rem(40)};
    }
  }
`

const OverviewSectionContainer = styled(LayoutContainer)`
  padding-top: ${rem(24)};
  padding-bottom: ${rem(24)};

  ${mediaQueryUp.sm} {
    padding-bottom: ${rem(64)};
    padding-top: ${rem(24)};
  }
`

const YearTabButtonsGroup = styled(TabButtonsGroup)`
  ${mediaQueryUp.sm} {
    margin-top: ${rem(16)};
    width: fit-content;
    max-width: ${rem(424)};
  }
`

const SectionTabButtonsGroup = styled(TabButtonsGroup)`
  background: ${props => props.theme.palette.neutral.eight};
`

const DepartureDates = styled(VerticalSpacer)`
  text-align: -webkit-left;
`

const OfferOverlayContainer = styled(LabelGroup)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const CABIN_INFO_DESCRIPTION: { [key in App.CabinCategoryType]: string } = {
  Inside: 'An inside cabin is normally the most affordable option, often without a window.',
  Oceanview: 'An oceanview cabin, often called an outside cabin, is a room with a port hole or window that looks out to sea, depending on deck and location.',
  Balcony: 'A balcony cabin is any cruise room with a private verandah, allowing you to take in the beautiful views and fresh sea air.',
  Suite: 'A suite is an especially large room within a cruise ship\'s top tier of cabins, often with a wide window and/or private verandah.',
}

const getArrivalDate = (date: string, duration: number) => {
  const dateParsed = convertTZ(new Date(date), 'UTC')
  return addDays(dateParsed, duration)
}

const getDepartureInfo = (
  id: string,
  duration: number,
  departureDate: string,
): App.CruiseDepartureInfo => {
  const arrivalDate = getArrivalDate(departureDate, duration)
  const arrivalISODate = isoDateFormat(arrivalDate)

  return {
    departureDate,
    departureId: id,
    arrivalDate: arrivalISODate,
  }
}

type SectionTabs = {
  label: string;
  value: CruiseSectionTabValues;
}

type DeparturesByDate = {
  [date: string]: App.CruiseDepartureDetails[]
}

const hasDepartureThisYear = (departures: DeparturesByDate, year: string): boolean => {
  return Object.keys(departures)
    .filter(departureDate => departureDate.includes(year))?.length > 0
}

interface Props extends RouteComponentProps {
  cruiseItem: App.Checkout.CruiseItem;
  isSpoofed: boolean;
  departureId: string;
  previousRoute: string
  offer: App.CruiseOffer;
  travellers?: App.CruiseTraveler;
  shareModalConfig?: ShareModalConfig;
  isVelocityEnabled: boolean;
  currentRegionCode: string;
  place:{
    [key: string]: App.Place;
  };
}

function generateFilters(
  offerType: App.OfferType,
  departurePort: string,
  place:{
    [key: string]: App.Place;
  },
  cruiseLine: App.CruiseLine,
) {
  const filters = {} as any
  if (cruiseLine) {
    filters.cruiseLines = [cruiseLine.code]
  }
  if (departurePort) {
    filters.departurePlaceId = place[departurePort]?.id
  }
  if (offerType === OFFER_TYPE_CRUISE) {
    filters.offerTypes = [OFFER_TYPE_CRUISE]
  }
  return filters
}

function CruiseContent({
  cruiseItem,
  isSpoofed,
  offer,
  departureId,
  travellers,
  previousRoute,
  shareModalConfig,
  currentRegionCode,
  place,
}: Props) {
  const { itinerary, finePrint, departures, mainDepartureDetails } = offer
  const { departureDate: mainDepartureDate, lowestOverallPriceDetails, lowestPriceDetailsByCategory } = mainDepartureDetails
  const { promotion: promotionDetails } = lowestOverallPriceDetails

  const departuresDatesAnchor = useRef<HTMLDivElement>(null)
  const [hasGotToTabs, setHasGotToTabs] = useState<boolean>(false)
  const dispatch = useAppDispatch()

  useEffect(() => {
    const handleScroll = () => {
      const element = departuresDatesAnchor.current
      if (element) {
        const rect = element.getBoundingClientRect()
        setHasGotToTabs(rect.top - 180 <= 0)
      }
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    const availableCruiseRegions = config.STANDALONE_CRUISE_REGIONS?.split(',') ?? []
    if (!availableCruiseRegions.includes(currentRegionCode)) {
      dispatch(replaceWithRegion('/'))
    }
  }, [dispatch, currentRegionCode])

  const params = useQueryParams()
  const defaultDepartureDate = useMemo(() => {
    // if we have a main departure date, use that
    if (mainDepartureDate) return mainDepartureDate.split('-').slice(0, 2).join('-')

    // find the earliest departure within the months we've been given for the departures we have
    const months = params.getAll('departureMonths')
    if (months.length > 0) {
      const monthsWithDepartures = months.filter(month => !!departures[month])
      return min(monthsWithDepartures, month => new Date(`${month}-01`).getTime())
    }

    return undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (params.getAll('departureMonths').length > 0) {
      // remove params on page landing, it was only for initialisation
      const nextParams = new URLSearchParams(params)
      nextParams.delete('departureMonths')
      dispatch(replace({ search: nextParams.toString() }))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [monthTab, setMonthTab] = useState<string>(defaultDepartureDate ?? Object.keys(departures).sort()[0])

  const [showCabinModal, onToggleCabinModal] = useToggle()
  const [showCabinInfoModal, onToggleCabinInfoModal] = useToggle()
  const [cabinCategory, setCabinCategory] = useState<App.CabinCategoryType>('Inside')
  const [departureSelected, setDepartureSelected] = useState<App.CruiseDepartureDetails>()
  const [occupancies, setOccupancies] = useState<Array<App.Occupants>>(OCCUPANCY_INITIAL_STATE)
  const [currentSectionTab, setCurrentSectionTab] = useState<CruiseSectionTabValues>(CruiseSectionTabValues.DATES_PRICING)
  const [selectedDepartureYear, setYear] = useState(defaultDepartureDate?.substring(0, 4) ?? Object.keys(departures).sort()[0]?.substring(0, 4))

  const arrayData = useMemo(() => arrayOfYears(5), [])

  const [showTravelersModal, onToggleTravelersModal] = useToggle()

  const tabDepartures = useMemo(() => {
    return (departures[monthTab] || [])
      .sort((a, b) => {
        if (a.departureDate && b.departureDate) {
          return new Date(a.departureDate).getTime() - new Date(b.departureDate).getTime()
        } else return 0
      })
  }, [departures, monthTab])

  useEffect(() => {
    dispatch(resetCabinRates())
    startExperiment(EXPERIMENT.CRUISES_PRICES_PER_NIGHT)
  }, [dispatch])
  useEffect(() => {
    dispatch(fetchPlaceByName(offer.departurePort))
  }, [dispatch, offer])

  const contentSections = useMemo(
    () => determineCruiseContentSections(offer),
    [offer],
  )

  const departureYears = Object.keys(departures).reduce(
    (set, departureDate) => (set.add(departureDate.split('-')[0])),
    new Set<string>(),
  )

  const month = useMemo(() => moment(monthTab).format('MMMM YYYY'), [monthTab])
  const availableMonths = useMemo(
    () => Object.keys(departures).filter((i) => i.includes(selectedDepartureYear)),
    [departures, selectedDepartureYear],
  )

  const onCabinInfoModal = useCallback((cabinType: App.CabinCategoryType) => {
    setCabinCategory(cabinType)
    onToggleCabinInfoModal()
  }, [onToggleCabinInfoModal])

  const onOccupanciesChange = useCallback((
    occupants: Array<App.Occupants>,
  ) => setOccupancies(occupants), [setOccupancies])

  const onBackModal = useCallback(() => {
    onToggleCabinModal()
    onToggleTravelersModal()
  }, [onToggleCabinModal, onToggleTravelersModal])

  const getCabinRates = useCallback((travelerInfo: App.Occupants, departure: App.CruiseDepartureDetails) => {
    if (departure.departureDate) {
      const { departureId, arrivalDate, departureDate } = getDepartureInfo(
        departure.id, offer.duration, departure.departureDate)

      dispatch(fetchCabinRates({
        departureId,
        arrivalDate,
        departureDate,
        travelers: {
          adult: travelerInfo.adults,
          child: (travelerInfo.children ?? 0) + (travelerInfo.infants ?? 0),
          childAge: travelerInfo.childrenAge ?? [],
        },
      }))
    }
  }, [offer.duration, dispatch])

  const onContinue = useCallback(() => {
    onToggleTravelersModal()
    onToggleCabinModal()

    if (occupancies[0]?.adults && departureSelected) {
      getCabinRates(occupancies[0], departureSelected)
    }
  }, [occupancies, departureSelected, getCabinRates, onToggleTravelersModal, onToggleCabinModal])

  const onHandleDepartureModal = useCallback((departure: App.CruiseDepartureDetails) => {
    fireInteractionEvent(cruiseSelectDepartureClick(departure.id))

    if (config.CRUISES_NEW_CHECKOUT_ENABLED && departure.departureDate && departure.arrivalDate) {
      const dates = {
        startDate: departure.departureDate,
        endDate: departure.arrivalDate,
      }

      const item = generateCruiseCheckoutItem(
        offer.id,
        departure.id,
        dates,
        { adults: 0, children: 0, infants: 0 },
      )

      if (cruiseItem) {
        // cruise can only support one cruise item in checkout cart
        dispatch(removeItem(cruiseItem.itemId))
      }
      dispatch(addToCheckoutCart([item]))
      dispatch(setInFlowCheckoutStep(CRUISE_CHECKOUT_INFLOW.TRAVELERS))
      dispatch(pushWithRegion('/checkout/cruises'))
    } else {
      setDepartureSelected(departure)
      onToggleTravelersModal()
    }
  }, [dispatch, offer, onToggleTravelersModal, setDepartureSelected, cruiseItem])

  const handleYearChange = (departures: DeparturesByDate, year: string) => {
    const departureKey = Object.keys(departures)
      .find((key) => key.includes(year))

    setYear(String(year))
    if (departureKey) setMonthTab(departureKey)
  }
  const scrollToPackageOptions = useCallback((tab:string) => {
    if (tab === 'dates-and-pricing') {
      setCurrentSectionTab(CruiseSectionTabValues.DATES_PRICING)
    }
    departuresDatesAnchor.current?.scrollIntoView()
  }, [])

  const hasValidDepartures = useMemo(() => offerHasValidDepartures(offer), [offer])

  const SECTION_TABS: SectionTabs[] = useMemo(() => {
    const tabs: SectionTabs[] = [
      { label: 'Itinerary', value: CruiseSectionTabValues.ITINERARY },
      { label: 'About the ship', value: CruiseSectionTabValues.ABOUT_SHIP },
    ]
    if (hasValidDepartures) tabs.unshift({ label: 'Dates & Pricing', value: CruiseSectionTabValues.DATES_PRICING })
    if (contentSections.has(CruiseContentSections.FAQ)) tabs.push({ label: 'Frequently asked questions', value: CruiseSectionTabValues.FAQ })
    return tabs
  }, [contentSections, hasValidDepartures])

  const handleTabsChange = (tab: CruiseSectionTabValues) => setCurrentSectionTab(tab)
  const velocityFeatures = getApplicableVelocityFeatures(lowestOverallPriceDetails.total ?? 0)

  useEffect(() => {
    if (
      departureId &&
      !!(travellers?.adult) && travellers?.adult > 0 &&
      previousRoute?.includes('checkout/cabin-location') &&
      !isSpoofed
    ) {
      const departure = tabDepartures.find(d => d.id === departureId)
      if (departure) {
        onToggleCabinModal()
        setDepartureSelected(departure)
        getCabinRates({
          adults: travellers.adult,
          children: travellers.child,
          childrenAge: travellers.childAge,
        }, departure)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const recommendationFilters = useMemo(() =>
    offer ? generateFilters(
      offer.type,
      offer.departurePort,
      place,
      offer.cruiseLine,
    ) : {}
  , [place, offer])

  return (
    <>
      <CruiseHeadData offer={offer} />
      <CruiseStickyHeader
        onView={scrollToPackageOptions}
        priceFrom={lowestOverallPriceDetails}
        visible={hasGotToTabs}
        title={offer.name}
        tabs={SECTION_TABS}
        currentSectionTab={currentSectionTab}
        handleTabsChange={handleTabsChange}
      />
      <div id="CRUISE_CONTENT_TOP_ANCHOR" aria-hidden="true"></div>

      {contentSections.has(CruiseContentSections.GALLERY) && (
        <Pane>
          <GalleryContainer>
            <ImageGallery
                images={offer.images}
                arrowBehaviour="always-visible"
                topLeft={<OfferOverlayContainer>
                  <ProductTypeLabel productType={offer.productType} />
                  <PartnershipLabel canEarn={velocityFeatures.canEarn} canUse={velocityFeatures.canBurn} variant="icon" />
                </OfferOverlayContainer>
                }
              />
          </GalleryContainer>
        </Pane>
      )}

      <Pane>
        <OverviewSectionContainer>
          <VerticalSpacer gap={16}>
            {!!promotionDetails?.isNew && <UrgencyLabel variant="speed">NEW</UrgencyLabel>}
            {!!promotionDetails?.isEndingIn && promotionDetails?.isEndingIn <= 7 && <UrgencyLabel variant="critical2">OFFER ENDS IN {pluralizeToString('day', promotionDetails.isEndingIn)} </UrgencyLabel>}
            <CruiseOverview
              offer={offer}
              showPackageOptions={() => scrollToPackageOptions('dates-and-pricing')}
              shareModalConfig={shareModalConfig}
            />
          </VerticalSpacer>
        </OverviewSectionContainer>
      </Pane>

      <Pane>
        <ScrollAnchor ref={ departuresDatesAnchor} />
        <LayoutContainer>
          <SectionTabButtonsGroup fit="auto">
            {SECTION_TABS.map((tab) => (
              <TabButton
                size="medium"
                key={tab.value}
                isActive={tab.value === currentSectionTab}
                onClick={() => handleTabsChange(tab.value)}>{tab.label}</TabButton>
            ))}
          </SectionTabButtonsGroup>
        </LayoutContainer>
      </Pane>

      {currentSectionTab === 'DATES_PRICING' && (
        <Pane className="type-light-grey">
          <SectionContainer size="md">
            <Heading variant="heading2" align="center" className="section-title">
              {CRUISE_CONTENT_SECTION_LABELS[CruiseContentSections.DEPARTURES]}
            </Heading>

            <DepartureDates gap={32}>

              {departureYears.size > 1 && <YearTabButtonsGroup fit="grow-at-mobile">
                {arrayData.map((year) => hasDepartureThisYear(departures, year) &&
                  <TabButton
                    key={year}
                    size="medium"
                    isActive={String(year) === selectedDepartureYear}
                    onClick={() => handleYearChange(departures, year)}
                  >{year}</TabButton>,
                )}
              </YearTabButtonsGroup>}

              <CruiseDepartureDatesTabs
                tab={monthTab}
                setTab={setMonthTab}
                departureDate={departures}
                year={selectedDepartureYear}
                cachedDepartures={lowestPriceDetailsByCategory}
              />

              {monthTab && availableMonths.length > 1 && <BodyText variant="large" weight="bold" align="start">{month}</BodyText>}

              {monthTab && tabDepartures.map((departure) => (
                <CruiseDepartures
                  cruise={offer}
                  key={departure.id}
                  departure={departure}
                  onCabinInfoModal={onCabinInfoModal}
                  onHandleDepartureModal={onHandleDepartureModal}
                />
              ))}

              <MultipleCabinsCallUs />
            </DepartureDates>
          </SectionContainer>
        </Pane>
      )}

      {currentSectionTab === 'ITINERARY' && (
        <Pane>
          {contentSections.has(CruiseContentSections.ITINERARY) && (
            <SectionContainer>
              <Heading
                align="center"
                variant="heading2"
                className="section-title">
                {CRUISE_CONTENT_SECTION_LABELS[CruiseContentSections.ITINERARY]}
              </Heading>
              <div className="section-content">
                <CruiseItineraryAndMap
                  itinerary={itinerary || []}
                />
              </div>
            </SectionContainer>
          )}
        </Pane>
      )}

      {currentSectionTab === 'ABOUT_SHIP' && (
        <>
          <Pane>
            <SectionContainer>
              <Heading
                align="center"
                variant="heading2"
                className="section-title">About the ship</Heading>
              <div className="section-content"><AboutTheShip offer={offer} /></div>
            </SectionContainer>
          </Pane>

          {contentSections.has(CruiseContentSections.DECKS_AND_CABINS) && (
            <Pane>
              <SectionContainer>
                <Heading
                  align="center"
                  variant="heading2"
                  className="section-title"
                >{CRUISE_CONTENT_SECTION_LABELS[CruiseContentSections.DECKS_AND_CABINS]}</Heading>

                <div className="section-content">
                  <CruiseDecksAndCabins
                    ship={offer.ship}
                    hasGotToOverview={hasGotToTabs}
                    showPackageOptions={() => scrollToPackageOptions('dates-and-pricing')} />
                </div>
              </SectionContainer>
            </Pane>
          )}

          {!!finePrint && contentSections.has(CruiseContentSections.FINE_PRINT) && (
            <Pane>
              <SectionContainer size="md">
                <Heading
                  align="center"
                  variant="heading2"
                  className="section-title"
                >{CRUISE_CONTENT_SECTION_LABELS[CruiseContentSections.FINE_PRINT]}</Heading>
                <div className="section-content"><CruiseFinePrint finePrint={finePrint} /></div>
              </SectionContainer>
            </Pane>
          )}
        </>
      )}

      {currentSectionTab === 'FAQ' && (
        <Pane>
          <SectionContainer size="md">
            <Heading
              align="center"
              variant="heading2"
              className="section-title"
            >{CRUISE_CONTENT_SECTION_LABELS[CruiseContentSections.FAQ]}</Heading>

            <div className="section-content" data-testid="faq-section">
              <CruiseFAQ ship={offer.ship} cruiseLine={offer.cruiseLine} />
            </div>
          </SectionContainer>
        </Pane>
      )}
      <PaletteProvider type="inverse">
        <USPPane>
          <LayoutContainer>
            <Heading colour="neutral-one" variant="heading3" align="start">Why you should book with us</Heading>
            <CruiseVerticalUSPs/>
          </LayoutContainer>
        </USPPane>
      </PaletteProvider>

      <CruiseStickyFooter
        onView={scrollToPackageOptions}
        priceFrom={lowestOverallPriceDetails}
      />
      {recommendationFilters.departurePlaceId && <ErrorBoundary fallback={null}>
        <CruiseRecommendations offerId={offer.id} filters={recommendationFilters}/>
      </ErrorBoundary>}

      <Modal
        title={cabinCategory}
        isOpen={showCabinInfoModal}
        onClose={onToggleCabinInfoModal}>
        <BodyText weight="bold" variant="large">
          {' '}About this cabin{' '}
        </BodyText>
        {CABIN_INFO_DESCRIPTION[cabinCategory]}
      </Modal>

      {!!offer.ship.occupancies && (
        <TravelerSelectionModal
            onContinue={onContinue}
            occupancies={occupancies}
            isOpen={showTravelersModal}
            onClose={onToggleTravelersModal}
            shipOccupancies={offer.ship.occupancies}
            onOccupanciesChange={onOccupanciesChange}
          />
      )}

      {departureSelected && (
        <CabinSelectionModal
          offer={offer}
          onBack={onBackModal}
          isOpen={showCabinModal}
          travelersInfo={occupancies}
          onClose={onToggleCabinModal}
          departureId={departureSelected.id}
          cabinView={cabinViewMap(offer)}
        />
      )}
    </>
  )
}

function mapStateToProps(appState: App.State) {
  return {
    cruiseItem: getCruiseItems(appState)[0],
    isSpoofed: appState.auth.account.isSpoofed,
    travellers: appState.cruise.travelers,
    departureId: appState.cruise.departureId,
    previousRoute: appState.routeHistory.previous,
    isVelocityEnabled: appState.geo.partnership?.code === VIRGIN_VELOCITY_PARTNERSHIP_CODE,
    currentRegionCode: appState.geo.currentRegionCode,
    place: appState.destination.locationToPlace,
  }
}

export default withRouter(connect(mapStateToProps)(CruiseContent))
